import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppStateType } from 'reducers';
import * as ProfileService from 'ProfileModule/services';
import { archiveTeam } from 'ProfileModule/services/archiveTeamApi';
import { scheduleArchiveTeam } from 'ProfileModule/services/scheduleArchiveTeamApi';
import * as ReportsService from 'ReportsModule/services';
import ApiClientManager from 'core/apiClient';
import * as Types from 'ProfileModule/types';
import { TeamMembershipType } from 'types';

export const fetchTeamMembers = createAsyncThunk(
  'fetchTeamMembers',
  async (
    { teamId, tenant, realmId, ...queryParams }: Types.FetchTeamMembersParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      // Build custom headers if tenant/realmId are provided
      const customHeaders =
        tenant && realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
          : undefined;

      const response = await ProfileService.getTeamMember(
        teamId,
        {
          params: queryParams,
          ...(customHeaders && { headers: customHeaders }),
        },
        auth_token,
      );
      return (response.data as any).team_members;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

// Server-side paginated team-members fetch for the per-team "All Members" table
// (GLX-1740). Returns just the current page plus the total count; the reducer
// stores both. Distinct from `fetchTeamMembers`, which loads the full list for
// the Suppressed Emails tab.
export const searchTeamMembers = createAsyncThunk(
  'searchTeamMembers',
  async (
    { teamId, tenant, realmId, ...queryParams }: Types.SearchTeamMembersParams,
    { getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      // Build custom headers if tenant/realmId are provided
      const customHeaders =
        tenant && realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
          : undefined;

      const response = await ProfileService.searchTeamMembers(
        teamId,
        {
          params: queryParams,
          ...(customHeaders && { headers: customHeaders }),
        },
        auth_token,
      );
      const data = (response.data as any) || {};
      return {
        teamMembers: (data.team_members ?? []) as TeamMembershipType[],
        totalCount: (data.total_count ?? 0) as number,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const updateTeam = createAsyncThunk(
  'updateTeam',
  async (
    params: Types.UpdateTeamParams & { tenant?: string; realmId?: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      // Extract context params (don't send to API)
      const { tenant, realmId, ...apiParams } = params;

      // Build custom headers if tenant/realmId are provided
      const customHeaders =
        tenant && realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
          : undefined;

      const response = await ReportsService.updateTeam(
        apiParams,
        auth_token as string,
        customHeaders ? { headers: customHeaders } : {},
      );

      params.onSuccess?.({ response });

      return response.data;
    } catch (e) {
      params.onFailure?.({ error: e });
      return rejectWithValue(e);
    }
  },
);

/* -------------------------------------------------------------------------- */

export const updateTeamMember = createAsyncThunk(
  'updateTeamMember',
  async (params: Types.UpdateTeamMemberParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const { teamMembershipId, onSuccess } = params;
      const response = await ProfileService.updateTeamMember(
        teamMembershipId,
        params,
        {},
        auth_token as string,
      );
      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (e) {
      return e;
    }
  },
);

export const resetMemberPassword = createAsyncThunk(
  'resetMemberPassword',
  async ({ emails }: Types.resetMemberPasswordParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const body = {
        emails,
      };
      const response = await ProfileService.resetMemberPassword(body, {}, auth_token as string);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const provisionQbd = createAsyncThunk(
  'provisionQbd',
  async (params: Types.provisionQbdParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const response = await ProfileService.provisionQbd(params, {}, token as string);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchProvisionErrors = createAsyncThunk(
  'fetchProvisionErrors',
  async (
    { teamId, onSuccess, onFailure }: Types.fetchProvisionErrorsParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const response = await ProfileService.fetchProvisionErrors({ teamId }, {}, token as string);
      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const bulkActivateAccounts = createAsyncThunk(
  'bulkActivateAccounts',
  async (params: Types.bulkActivateAccountsParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const response = await ProfileService.bulkActivateAccounts(params, {}, token as string);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const deactivateTeams = createAsyncThunk(
  'deactivateTeams',
  async (
    {
      onSuccess,
      skipStateUpdate,
      dataRetentionDays,
      deactivationReason,
      teamTargets,
    }: Types.DeactivateTeamsParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const buildArchiveCall = ({
        teamId,
        tenant,
        realmId,
      }: {
        teamId: number;
        tenant?: string;
        realmId?: string;
      }) => {
        const archiveBody: {
          teamId: number;
          customizedDataPreservationPeriod?: number;
          reasonToArchive: string;
        } = {
          teamId,
          reasonToArchive: deactivationReason,
        };
        if (dataRetentionDays !== undefined) {
          archiveBody.customizedDataPreservationPeriod = dataRetentionDays;
        }
        const headerOverrides =
          tenant && realmId
            ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
            : undefined;
        return archiveTeam({
          params: archiveBody,
          token: token as string,
          config: headerOverrides ? { headers: headerOverrides } : {},
        });
      };

      const promises = (teamTargets || []).map(({ teamId, tenant, realmId }) =>
        buildArchiveCall({ teamId, tenant, realmId }),
      );
      const response = await Promise.allSettled(promises);

      if (onSuccess) {
        onSuccess({ response });
      }

      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const bulkSetUsersTemporaryPassword = createAsyncThunk(
  'bulkSetUsersTemporaryPassword',
  async (
    { accountIdAndPasswordPair, onSuccess, onFailure }: Types.bulkSetUsersTemporaryPasswordParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const promises = accountIdAndPasswordPair.map((item) =>
        ProfileService.setSingleUserTemporaryPassword(item, {}, token as string),
      );
      const response = await Promise.allSettled(promises);

      if (onSuccess) {
        onSuccess({ response });
      }

      return response;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const scheduleDeactivateTeams = createAsyncThunk(
  'scheduleDeactivateTeams',
  async (
    {
      onSuccess,
      dataRetentionDays,
      deactivationReason,
      teamTargets,
      scheduledArchiveDate,
    }: Types.ScheduleDeactivateTeamsParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const token = user?.auth_token;
      const buildScheduleArchiveCall = ({
        teamId,
        tenant,
        realmId,
      }: {
        teamId: number;
        tenant?: string;
        realmId?: string;
      }) => {
        const body: {
          teamId: number;
          customizedDataPreservationPeriod?: number;
          reasonToArchive: string;
          scheduledArchiveDate: string;
        } = {
          teamId,
          reasonToArchive: deactivationReason,
          scheduledArchiveDate,
        };
        if (dataRetentionDays !== undefined) {
          body.customizedDataPreservationPeriod = dataRetentionDays;
        }
        const headerOverrides =
          tenant && realmId
            ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
            : undefined;
        return scheduleArchiveTeam({
          params: body,
          token: token as string,
          config: headerOverrides ? { headers: headerOverrides } : {},
        });
      };

      const promises = (teamTargets || []).map(({ teamId, tenant, realmId }) =>
        buildScheduleArchiveCall({ teamId, tenant, realmId }),
      );
      const response = await Promise.allSettled(promises);

      if (onSuccess) {
        onSuccess({ response });
      }

      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
